<template>
  <div class="family-group-scope">
    <LoadingIndicator v-if="loadingRef" />
    <template v-else>
      <div class="help-text">
        <template v-if="enableEditRef">
          Select a set of common ancestors to help you choose group members.
        </template>
        <template v-else>
          Group members should be related to this family.
        </template>
      </div>
      <template v-if="showStepActionsRef">
        <div class="step-actions">
          <button type="button" class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#scopeModal">
            Select a family
          </button>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="emit('stepDismissed')">
            Skip
          </button>
        </div>
      </template>
      <div v-else class="scope-family">
        <img src="@/assets/icons/mini_tree_150.png" class="scope-icon"/>
        <ViewFamilySelector 
          ref="familySelectorRef"
          :model-value="familyGroupRef?.scopeFamilyId" 
          text-only
          placeholder="Type a name to search"
          :can-edit="enableEditRef" 
          :empty-text="emptyTextRef"
          @update:model-value="onScopeChange"/>
        <button type="button" v-if="showClearScopeRef" class="btn btn-inline btn-link clear" @click="onScopeChange()">
          <XIcon/>
        </button>
      </div>
      <button type="button" v-if="showStep && !showStepActionsRef" class="btn btn-inline btn-link" @click="showConfirmSkipModal">
        Skip this step
      </button>
    </template>
    <FamilyGroupScopeModal id="scopeModal"
      :family-group-id="familyGroupId"
      :candidate-family-ids="candidateFamilyIdsRef"
      @select-none="onSelectNone"/>
    <Modal ref="confirmSkipModalRef" class="confirm-skip-modal" ok-close @ok="emit('stepDismissed')">
      <template #title>Select common ancestors</template>
      <TipAlert>
         Selecting common ancestors will let you choose group members from a list of their descendants.
      </TipAlert>
      <div>
        Are you sure you want to skip this step?
      </div>
      <template #okText>Skip this step</template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.family-group-scope {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
}

.loading-indicator {
  margin-left: 1rem;
}

.btn-link {
  font-size: 0.875rem;
}

.scope-icon {
  filter: contrast(0.5) sepia(1) hue-rotate(130deg) brightness(2.0) saturate(3);
}

.scope-family {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;

  .scope-icon {
    width: 20px;
  }

  .clear {
    color: #aaa;

    &:hover {
      color: #666;
    }
  }
}

.tip-alert {
  margin: 0;
}

.step-actions {
  flex-grow: 0;
  display: grid;
  grid-template-columns: max-content max-content 1fr;
  column-gap: 1rem;
  row-gap: 1rem;

  .btn {
    min-width: 80px;
  }
}

.confirm-skip-modal {
  .scope-icon {
    width: 28px;
    margin-top: -4px;
    margin-right: 0.75rem;
  }

  :deep(.modal-body) {
    padding: 0 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .tip-alert {
    margin: 0;
  }
}
</style>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useTokenManager } from '@/auth/TokenManager'
import { LoadMode } from '@/util/AsyncData'
import { GroupMemberRole, ItemPermissions } from '@/gp/GroupAdminModel'
import { PatchChange } from '@/util/Api'
import { XIcon } from '@zhuowenli/vue-feather-icons'
import { isDefined } from '@/util/TypeScriptUtil'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import ViewFamilySelector from '@/manage/ViewFamilySelector.vue'
import FamilyGroupScopeModal from './FamilyGroupScopeModal.vue'
import Modal from '@/util/Modal.vue'
import TipAlert from '@/util/TipAlert.vue'

const props = defineProps<{
  familyGroupId: string
  showStep?: boolean
  suppressEdit?: boolean
}>()

const emit = defineEmits([
  'loaded',
  'stepDismissed',
])

const loadingRef = ref(false)
const permissionsRef = ref(ItemPermissions.None)
const candidateFamilyIdsRef = ref<string[]>([])

const familySelectorRef = ref<InstanceType<typeof ViewFamilySelector>>()
const confirmSkipModalRef = ref<InstanceType<typeof Modal>>()

const familyGroupStore = useFamilyGroupStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const dataGroupStore = useDataGroupStore()
const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const tokenManager = useTokenManager()

const asyncFamilyGroupRef = computed(() => familyGroupStore.getAsyncGroup(props.familyGroupId, LoadMode.EnsureLoaded))

const familyGroupRef = computed(() => asyncFamilyGroupRef.value?.data)
const isOwnerRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)
const showStepActionsRef = computed(() => props.showStep && candidateFamilyIdsRef.value.length > 0)
const enableEditRef = computed(() => isOwnerRef.value && !props.suppressEdit)
const emptyTextRef = computed(() => isOwnerRef.value && props.showStep ? 'Select a family' : undefined)
const showClearScopeRef = computed(() => !!familyGroupRef.value?.scopeFamilyId && enableEditRef.value)

watch(() => props.familyGroupId, async () => {
  permissionsRef.value = ItemPermissions.None
  loadingRef.value = true

  try {
    await asyncFamilyGroupRef.value?.whenLoadCompleted

    await viewFamilyStore.getFamilyAsync(familyGroupRef.value?.scopeFamilyId)

    const asyncPerms = familyGroupStore.getAsyncPermissions(props.familyGroupId, LoadMode.EnsureLoaded)
    permissionsRef.value = (await asyncPerms?.whenLoadCompleted) ?? ItemPermissions.None

    candidateFamilyIdsRef.value = []
    
    if (!familyGroupRef.value?.scopeFamilyId && isOwnerRef.value) {
      // find candidate scope families (parent families of this user who are not the scope family for any another family group owned by this user)
      const allGroupMembers = await familyGroupMemberStore.getMembersForUsersAsync([tokenManager.current.userId])
      const allOwnedGroupIds = allGroupMembers.filter(fm => fm.role == GroupMemberRole.Owner).map(fm => fm.familyGroupId)
      const allOwnedGroups = await familyGroupStore.getGroupListAsync(allOwnedGroupIds)
      const allScopeFamilyIds = new Set(allOwnedGroups.map(fg => fg.scopeFamilyId).filter(isDefined))

      const selfProfileGroup = await dataGroupStore.getAsyncGroup(tokenManager.current.userProfileId, LoadMode.EnsureLoaded)?.whenLoadCompleted
      if (selfProfileGroup?.startItemId) { // should always be true
        await viewPersonStore.ensureAncestorsLoadedAsync([selfProfileGroup.startItemId], 1)
        const parentFamilies = viewPersonStore.getLoadedParentFamilies(selfProfileGroup.startItemId).parentFamilies
        candidateFamilyIdsRef.value = parentFamilies.filter(vf => !vf.matchIds.some(id => allScopeFamilyIds.has(id))).map(vf => vf.id!)
      }
    }
  }
  finally {
    loadingRef.value = false
    emit('loaded')
  }
}, { immediate: true })

function showConfirmSkipModal() {
  confirmSkipModalRef.value?.open()
}

async function onSelectNone() {
  candidateFamilyIdsRef.value = []
  await nextTick()
  familySelectorRef.value?.showSearchInput()
}

async function onScopeChange(newFamilyId?: string) {
  if (newFamilyId != familyGroupRef.value?.scopeFamilyId) {
    const change = new PatchChange("/scopeFamilyId", newFamilyId)
    await familyGroupStore.patchAsync(props.familyGroupId!, [change])
  }
}
</script>
